import React from 'react';
import PropTypes from 'prop-types';
import {
  CheckCircle,
  Phone,
  Calendar,
  SmileyWink,
} from '@phosphor-icons/react';
import Typography from '../Typography';
import { getStatusIndex } from './statusUtils';
import './referralStatusStepper.scss';

// Define the status progression with icons and labels
const STATUS_STEPS = [
  { key: 'received', label: 'Received', icon: CheckCircle },
  { key: 'contacted', label: 'Contacting Patient', icon: Phone },
  { key: 'scheduled', label: 'Scheduled', icon: Calendar },
  { key: 'seen', label: 'Seen/Visit Held', icon: SmileyWink },
];

const ReferralStatusStepper = ({ currentStatus, statusDates, hasFinalStep }) => {
  const steps = hasFinalStep !== false
    ? STATUS_STEPS
    : STATUS_STEPS.filter((s) => s.key !== 'seen');
  const currentStepIndex = getStatusIndex(currentStatus);
  const isTerminal = currentStepIndex === -1;

  const formatDate = (dateString) => {
    if (!dateString) return 'Pending';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="referral-status-stepper">
      <Typography variant="text" size="xsmall" className="referral-status-stepper__label">
        STATUS
      </Typography>

      <div className="referral-status-stepper__track">
        {steps.map((step, index) => {
          const IconComponent = step.icon;
          const isCompleted = !isTerminal && index < currentStepIndex;
          const isCurrent = !isTerminal && index === currentStepIndex;
          const isPending = !isTerminal && index > currentStepIndex;
          const stepDate = statusDates?.[step.key];

          let stepClass = 'referral-status-stepper__step';
          if (isTerminal) {
            stepClass += ' referral-status-stepper__step--error';
          } else if (isCompleted) {
            stepClass += ' referral-status-stepper__step--completed';
          } else if (isCurrent) {
            stepClass += ' referral-status-stepper__step--current';
          } else if (isPending) {
            stepClass += ' referral-status-stepper__step--pending';
          }

          return (
            <React.Fragment key={step.key}>
              <div className={stepClass}>
                <div className="referral-status-stepper__icon">
                  <IconComponent size={20} weight={isCompleted || isCurrent ? 'fill' : 'regular'} />
                </div>
                <Typography
                  variant="text"
                  size="xsmall"
                  className="referral-status-stepper__step-label"
                >
                  {step.label}
                </Typography>
                <Typography
                  variant="text"
                  size="xsmall"
                  className="referral-status-stepper__step-date"
                >
                  {isCompleted || isCurrent ? formatDate(stepDate) : 'Pending'}
                </Typography>
              </div>

              {index < steps.length - 1 && (
                <div
                  className={`referral-status-stepper__line ${
                    !isTerminal && index < currentStepIndex
                      ? 'referral-status-stepper__line--completed'
                      : ''
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

ReferralStatusStepper.propTypes = {
  currentStatus: PropTypes.string,
  statusDates: PropTypes.shape({
    received: PropTypes.string,
    contacted: PropTypes.string,
    scheduled: PropTypes.string,
    seen: PropTypes.string,
  }),
  hasFinalStep: PropTypes.bool,
};

ReferralStatusStepper.defaultProps = {
  currentStatus: '',
  statusDates: {},
  hasFinalStep: true,
};

export default ReferralStatusStepper;
